import * as React from 'react';
import { markGuideSeen } from '../lib/guide-api.js';
import { t, type MessageKey } from '@reos/i18n';
import { Link } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft } from 'lucide-react';
import { V4Group, V4Row } from '../components/ux-v4.js';
import { DataStep } from '../components/listing-draft/DataStep.js';
import { PublishStep } from '../components/listing-draft/PublishStep.js';
import { systemClock } from '@reos/config';
import { DomainApiError } from '../lib/api-error.js';
import { listCases } from '../lib/cases-api.js';
import { listMembers } from '../lib/members-api.js';
import { buildCaseRows } from '../lib/case-rows.js';
import { loadCaseCovers } from '../lib/case-covers.js';
import { stageLabel } from '../lib/case-stages.js';
import {
  createListingVideo,
  fetchListingDraft,
  fetchListingSocialCaption,
  fetchListingVideos,
  generateListingDescription,
  generateListingText,
  publishListingDraft,
  saveListingDraft,
  translateListingDraft,
  type DraftPatch,
  type ListingDescriptionDraft,
  type ListingDraftView,
  type ListingTranslation,
  type ListingTranslationLanguage,
  type ListingVideoMusicMood,
  type ListingVideoOrientation,
  type PublishResult,
} from '../lib/listing-draft-api.js';
import {
  fetchPropertyMedia,
  reorderPropertyMedia,
  setPropertyMediaCaption,
  uploadPropertyMedia,
} from '../lib/properties.js';
import '../styles/inzerat.css';

/**
 * Krok UXV4b — NOVY INZERAT Z PROVERENI, dva kroky (predloha
 * `docs/design/ux-v4/png/Inzerat.png` + `Zverejnit.png`):
 *
 *   1 ze 2  Údaje     — co REOS vyplnil (s puvodem), Doplň, Kontrola → Pokračovat
 *   2 ze 2  Zveřejnit — text, fotky, portaly → Zveřejnit na N portálech
 *
 * Jedna obrazovka = jedna prace = jedno hlavni tlacitko (rezim soustredeni,
 * bez menu — `isFocusModeRoute`). Vstup: Dalsi krok detailu zakazky
 * („Připravit inzerát") a „Nová" (vyber zakazky, `InzeratPickerPage`).
 */

const cs = { locale: 'cs' } as const;

/** Krok UI1 — kódy brány kreditu (`aiGateProblem` v `@reos/ai`, SSOT se `/v1/ai/extract-lv`). */
const AI_GATE_CREDITS_OUT = 'https://docs.reos.local/errors/ai-credits-exhausted';
const AI_GATE_KEY_INVALID = 'https://docs.reos.local/errors/ai-key-invalid';
const AI_GATE_DISABLED = 'https://docs.reos.local/errors/ai-disabled';

function errorText(error: unknown, fallback: MessageKey): string {
  if (error instanceof DomainApiError && error.type === 'listing-draft/ai-unavailable') {
    return t('webAdmin.listingDraft.publish.aiUnavailable', cs);
  }
  if (error instanceof DomainApiError && error.type === 'listing-draft/invalid-value') {
    return t('webAdmin.listingDraft.error.invalidValue', cs);
  }
  /*
   * Krok UXP5 bod 3: chyba modelu se neuklada jako text inzeratu (server ji
   * odmitne jako `ai-unusable`) — makler dostane cesky duvod, ne anglicky JSON.
   */
  if (error instanceof DomainApiError && (error.type ?? '').endsWith('listing-draft/ai-unusable')) {
    return t('webAdmin.listingDraft.publish.aiUnusable', cs);
  }
  if (error instanceof DomainApiError && error.type === AI_GATE_CREDITS_OUT) {
    return t('webAdmin.listingDraft.publish.aiDescribeCreditsOut', cs);
  }
  if (error instanceof DomainApiError && (error.type === AI_GATE_KEY_INVALID || error.type === AI_GATE_DISABLED)) {
    return t('webAdmin.listingDraft.publish.aiDescribeUnavailable', cs);
  }
  return t(fallback, cs);
}

export function InzeratPage({ caseId }: { readonly caseId: string }): React.JSX.Element {
  const queryClient = useQueryClient();
  const [step, setStep] = React.useState<'data' | 'publish'>('data');
  /*
   * Krok UXP4b bod 3 (nalez P2-8): krok 1 ukazuje VSECHNY udaje najednou —
   * povinna pole portalu se makléř do UXP4b dozvidal po kapkach az na kroku 2.
   * Sbalit jde dal tlacitkem „Méně údajů".
   */
  const [showAll, setShowAll] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [aiError, setAiError] = React.useState<string | null>(null);
  const [publishing, setPublishing] = React.useState(false);
  const [result, setResult] = React.useState<PublishResult | null>(null);

  const draftKey = ['listing-draft', caseId] as const;
  const draft = useQuery({ queryKey: draftKey, queryFn: () => fetchListingDraft(caseId) });
  const propertyId = draft.data?.propertyId ?? null;
  const media = useQuery({
    queryKey: ['property-media', propertyId],
    queryFn: () => fetchPropertyMedia(propertyId ?? ''),
    enabled: propertyId !== null,
  });
  const photos = (media.data ?? []).filter((item) => item.kind === 'photo');

  /*
   * Krok UI1b — STAV KRATKYCH VIDEI. Render bezi ve fronte workera, takze
   * obrazovka se dotahuje, dokud nejake video ceka (PENDING/RUNNING);
   * jakmile je vse hotove nebo nic nebezi, dotahovani se VYPNE — jinak by
   * otevreny inzerat tloukl API kazdych 5 s navzdy.
   */
  const videoKey = ['listing-video', caseId] as const;
  const videos = useQuery({
    queryKey: videoKey,
    queryFn: () => fetchListingVideos(caseId),
    enabled: step === 'publish',
    refetchInterval: (query) =>
      (query.state.data?.items ?? []).some((item) => item.status === 'PENDING' || item.status === 'RUNNING')
        ? 5_000
        : false,
  });

  const createVideo = async (
    orientation: ListingVideoOrientation,
    musicMood: ListingVideoMusicMood | null,
  ): Promise<void> => {
    setAiError(null);
    try {
      await createListingVideo(caseId, orientation, musicMood);
      await videos.refetch();
    } catch (cause) {
      setAiError(errorText(cause, 'webAdmin.listingDraft.error.save'));
    }
  };

  const setView = (view: ListingDraftView): void => {
    queryClient.setQueryData(draftKey, view);
  };

  const save = async (patch: DraftPatch): Promise<void> => {
    setError(null);
    setResult(null);
    try {
      setView(await saveListingDraft(caseId, patch));
    } catch (cause) {
      setError(errorText(cause, 'webAdmin.listingDraft.error.save'));
    }
  };

  const generate = async (mode: 'template' | 'ai'): Promise<void> => {
    setAiError(null);
    try {
      setView(await generateListingText(caseId, mode));
    } catch (cause) {
      setAiError(errorText(cause, 'webAdmin.listingDraft.error.save'));
    }
  };

  const describe = async (): Promise<ListingDescriptionDraft | null> => {
    setAiError(null);
    try {
      return await generateListingDescription(caseId);
    } catch (cause) {
      setAiError(errorText(cause, 'webAdmin.listingDraft.error.save'));
      return null;
    }
  };

  /*
   * Krok NAP1 bod 2 — PREKLAD INZERATU (EN/DE). Chyby jdou tutez cestou jako
   * „Navrhnout popis" (brana kreditu `aiGateProblem`, `errorText`), protoze
   * je to tataz AI cesta — jen jiny nastroj.
   */
  const translate = async (language: ListingTranslationLanguage): Promise<ListingTranslation | null> => {
    setAiError(null);
    try {
      return await translateListingDraft(caseId, language);
    } catch (cause) {
      setAiError(errorText(cause, 'webAdmin.listingDraft.error.save'));
      return null;
    }
  };

  /** Krok NAP1 bod 3 — text pro sítě (bez AI, deterministicky ze serveru). */
  const socialCaption = async (): Promise<string | null> => {
    setAiError(null);
    try {
      return (await fetchListingSocialCaption(caseId)).caption;
    } catch (cause) {
      setAiError(errorText(cause, 'webAdmin.listingDraft.error.save'));
      return null;
    }
  };

  const publish = (): void => {
    setPublishing(true);
    setError(null);
    void publishListingDraft(caseId)
      .then((outcome) => {
        setResult(outcome);
        setView(outcome.view);
      })
      .catch((cause: unknown) => setError(errorText(cause, 'webAdmin.listingDraft.error.publish')))
      .finally(() => setPublishing(false));
  };

  const view = draft.data;
  const header = (
    <header className="rh-ld-nav" data-testid="ld-nav">
      {step === 'data' ? (
        <Link to="/zakazky/$id" params={{ id: caseId }} className="rh-ld-nav__back" data-testid="ld-cancel">
          {t('webAdmin.listingDraft.nav.cancel', cs)}
        </Link>
      ) : (
        <button type="button" className="rh-ld-nav__back" onClick={() => setStep('data')} data-testid="ld-back">
          <ChevronLeft size={24} strokeWidth={2.4} aria-hidden="true" />
          {t('webAdmin.listingDraft.nav.back', cs)}
        </button>
      )}
      <span className="rh-ld-nav__title">
        {step === 'data' ? t('webAdmin.listingDraft.nav.title', cs) : t('webAdmin.listingDraft.nav.publishTitle', cs)}
      </span>
      <span className="rh-ld-nav__step" data-testid="ld-step">
        {t('webAdmin.listingDraft.nav.step', { ...cs, values: { step: step === 'data' ? 1 : 2 } })}
      </span>
    </header>
  );

  return (
    <div className="rh-ld" data-testid="ld-page" data-guide="listing">
      {header}
      <main className="rh-ld-main">
        {draft.isLoading ? <p className="rh-ld-foot">{t('webAdmin.listingDraft.loading', cs)}</p> : null}
        {draft.isError ? (
          <p className="rh-ld-error" role="alert">
            {t('webAdmin.listingDraft.error.load', cs)}
          </p>
        ) : null}
        {error !== null ? (
          <p className="rh-ld-error" role="alert" data-testid="ld-error">
            {error}
          </p>
        ) : null}
        {view !== undefined && step === 'data' ? (
          <DataStep key={showAll ? 'all' : 'main'} view={view} onSave={save} showAllInitially={showAll} />
        ) : null}
        {view !== undefined && step === 'publish' ? (
          <PublishStep
            view={view}
            media={photos}
            onSave={save}
            onGenerateText={generate}
            onDescribe={describe}
            onTranslate={translate}
            onSocialCaption={socialCaption}
            onTakePhoto={async (files) => {
              if (propertyId === null) return;
              await uploadPropertyMedia(propertyId, files);
              await media.refetch();
            }}
            onMoveFirst={async (mediaId) => {
              if (propertyId === null) return;
              const order = [mediaId, ...(media.data ?? []).map((item) => item.id).filter((id) => id !== mediaId)];
              queryClient.setQueryData(['property-media', propertyId], await reorderPropertyMedia(propertyId, order));
            }}
            {...(propertyId === null
              ? {}
              : {
                  /* Krok NOC3 bod 2 (nalez N19): popisek fotky pro portaly zadava
                     makler — nahrani uz nazev souboru neuklada. */
                  onSetCaption: async (mediaId: string, caption: string) => {
                    await setPropertyMediaCaption(propertyId, mediaId, caption);
                    await media.refetch();
                  },
                })}
            onPublish={publish}
            onFixFields={() => {
              setShowAll(true);
              setStep('data');
            }}
            publishing={publishing}
            result={result}
            aiError={aiError}
            {...(videos.data === undefined ? {} : { videos: videos.data })}
            onCreateVideo={createVideo}
          />
        ) : null}
      </main>
      {view !== undefined && step === 'data' ? (
        <footer className="rh-ld-footer">
          <button
            type="button"
            className="rh-v4-btn rh-v4-btn--filled rh-v4-btn--block rh-ld-cta__btn"
            onClick={() => {
              setStep('publish');
              // Krok PRU1 (ROZHODNUTI PRU1-6): inzerat pripraveny k odeslani = krok pruvodce.
              void markGuideSeen('listing');
            }}
            data-testid="ld-continue"
          >
            {t('webAdmin.listingDraft.data.continue', cs)}
          </button>
        </footer>
      ) : null}
    </div>
  );
}

/**
 * VYBER ZAKAZKY pro novy inzerat (nalez GPT 15).
 *
 * Z detailu zakazky se tenhle krok PRESKAKUJE — „Připravit inzerát" vede
 * rovnou na `/zakazky/:id/inzerat`. Samostatny vyber (`/inzerat`) zustava pro
 * pripad, kdy makler zacina od inzeratu, a od kroku UXZ1 nese to, podle ceho
 * se zakazka pozna: hledani, adresu, odpovedneho maklere, stav a nahled
 * nemovitosti. Radek stoji na TOMTEZ modelu jako seznam zakazek
 * (`lib/case-rows.ts` + `lib/case-covers.ts`), ne na druhem vypoctu.
 */
export function InzeratPickerPage(): React.JSX.Element {
  const [query, setQuery] = React.useState('');
  const picked = useQuery({
    queryKey: ['listing-draft-picker'],
    queryFn: async () => {
      const [sale, rent, members] = await Promise.all([
        listCases({ type: 'brokerage_sale', limit: 50 }),
        listCases({ type: 'brokerage_rent', limit: 50 }),
        listMembers(),
      ]);
      const items = [...sale.items, ...rent.items];
      const covers = await loadCaseCovers(
        items.map((item) => item.propertyId).filter((id): id is string => id !== null),
      );
      return buildCaseRows({
        cases: items,
        ownerName: (userId) => members.find((m) => m.userId === userId)?.name ?? null,
        displayName: (item) => ({ text: item.title, hint: undefined }),
        coverByProperty: covers,
        now: systemClock.now(),
      });
    },
  });

  const needle = query.trim().toLocaleLowerCase('cs');
  const rows = (picked.data ?? []).filter(
    (row) =>
      needle === '' ||
      row.name.toLocaleLowerCase('cs').includes(needle) ||
      (row.ownerName ?? '').toLocaleLowerCase('cs').includes(needle),
  );

  return (
    <div className="rh-ld" data-testid="ld-picker" data-guide="listing">
      <header className="rh-ld-nav">
        <Link to="/zakazky" className="rh-ld-nav__back">
          {t('webAdmin.listingDraft.nav.cancel', cs)}
        </Link>
        <span className="rh-ld-nav__title">{t('webAdmin.listingDraft.nav.title', cs)}</span>
        <span />
      </header>
      <main className="rh-ld-main">
        <p className="rh-ld-foot">{t('webAdmin.listingDraft.picker.lede', cs)}</p>
        <input
          type="search"
          className="rh-input"
          placeholder={t('webAdmin.listingDraft.picker.search', cs)}
          aria-label={t('webAdmin.listingDraft.picker.search', cs)}
          value={query}
          onChange={(event) => { setQuery(event.target.value); }}
          data-testid="ld-picker-search"
        />
        <V4Group testId="ld-picker-list">
          {rows.map((row) => (
            <V4Row
              key={row.id}
              icon={
                row.coverUrl === null ? null : (
                  <span className="rh-v4-thumb rh-v4-thumb--row" aria-hidden="true">
                    <img src={row.coverUrl} alt="" data-testid={`ld-pick-thumb-${row.id}`} />
                  </span>
                )
              }
              title={row.name}
              sub={[row.ownerName, stageLabel(row.stage)].filter((part): part is string => part !== null).join(' · ')}
              chevron
              testId={`ld-pick-${row.id}`}
              render={(className, content) => (
                <Link to="/zakazky/$id/inzerat" params={{ id: row.id }} className={className}>
                  {content}
                </Link>
              )}
            />
          ))}
          {picked.isSuccess && rows.length === 0 ? (
            <V4Row title={t('webAdmin.listingDraft.picker.empty', cs)} testId="ld-picker-empty" />
          ) : null}
        </V4Group>
      </main>
    </div>
  );
}
